import { useRef, useState } from "react";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, ArrowUpRight, Home, Building2, Award, Users, Sparkles, Shield, MessageCircle, MapPin, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OWNER_IMAGE = "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=1600&q=80";

const COHOST_SERVICES = [
  "Airbnb listing creation and optimization",
  "Pricing and availability management",
  "Guest inquiries and reservation coordination",
  "Check-in and check-out management",
  "Housekeeping and turnover coordination",
  "Guest communication and support",
  "Maintenance coordination",
  "Property performance reporting",
  "Owner dashboard transparency",
];

const LEASING_SERVICES = [
  "Negotiated leasing arrangements",
  "Furnished accommodation management",
  "Guest screening and coordination",
  "Housekeeping and property care",
  "Maintenance coordination",
  "Extended-stay occupancy management",
];

const TRUST_POINTS = [
  { icon: Award, title: "3 Consecutive Years as Airbnb Superhost", suffix: "" },
  { icon: Home, title: "Top 5 Ranked Homes", suffix: "(subject to ranking verification)" },
  { icon: MapPin, title: "Local Austin Hospitality Expertise", suffix: "" },
  { icon: Sparkles, title: "Dedicated Housekeeping Operations", suffix: "" },
  { icon: MessageCircle, title: "Professional Guest Communication", suffix: "" },
  { icon: Shield, title: "Transparent Owner Partnerships", suffix: "" },
];

export default function Owners() {
  const ref = useRef(null);
  const formRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

  const scrollToForm = (preselect) => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (preselect) {
      // Signal to the form via a custom event; form listens below.
      window.dispatchEvent(new CustomEvent("owners-preselect", { detail: preselect }));
    }
  };

  return (
    <div data-testid="owners-page" className="pt-28 md:pt-32">
      {/* Hero */}
      <section ref={ref} className="relative px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="text-xs sm:text-sm tracking-[0.2em] uppercase font-medium text-[#8A9A86]">For property owners</span>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light leading-[1.02] text-[#1C1917] max-w-4xl">
              Partner With Masaya Hospitality
            </h1>
            <p className="mt-8 text-lg text-[#44403C] leading-relaxed font-light max-w-3xl">
              Turn your property into a professionally managed hospitality investment. Masaya
              Hospitality partners with homeowners and real estate investors across the Austin area
              through flexible leasing and Airbnb co-hosting partnerships. We handle the day-to-day
              hospitality operations while you enjoy the benefits of property ownership.
            </p>
            <button onClick={() => scrollToForm("Not Sure Yet")} data-testid="owners-consultation-top" className="mt-10 inline-flex items-center gap-2 rounded-full bg-[#1C1917] text-white px-8 py-3.5 hover:bg-[#44403C] transition-colors duration-300 font-medium text-sm">
              Request a Partnership Consultation <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
            </button>
          </Reveal>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="mt-14 relative rounded-2xl overflow-hidden aspect-[16/9] border border-[#E7E5E4]"
          >
            <motion.img src={OWNER_IMAGE} alt="A professionally managed Masaya property" style={{ y, scale }} className="w-full h-[116%] object-cover -mt-[8%]" />
          </motion.div>
        </div>
      </section>

      {/* Partnership options */}
      <section className="px-6 md:px-12 py-24 md:py-32">
        <div className="max-w-7xl mx-auto">
          <Reveal className="mb-14 max-w-2xl">
            <span className="text-xs sm:text-sm tracking-[0.2em] uppercase font-medium text-[#8A9A86]">Two ways to partner</span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-[#1C1917]">
              Choose the partnership that fits your property
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Co-Hosting */}
            <Reveal>
              <div className="h-full rounded-2xl border border-[#E7E5E4] bg-[#FAFAF9] p-8 md:p-10 flex flex-col" data-testid="option-cohosting">
                <Users className="w-6 h-6 text-[#8A9A86]" strokeWidth={1.5} />
                <p className="mt-4 text-xs tracking-[0.2em] uppercase font-medium text-[#8A9A86]">Option 1 · Airbnb Co-Hosting Partnership</p>
                <h3 className="mt-3 text-2xl md:text-3xl tracking-tight font-light text-[#1C1917]">Your property. Our expertise. Shared success.</h3>
                <p className="mt-4 text-[#44403C] font-light leading-relaxed">
                  Already own a property but don't have the time or experience to manage Airbnb?
                  Masaya Hospitality provides professional co-hosting services designed to maximize
                  your property's potential while delivering exceptional guest experiences.
                </p>
                <ul className="mt-6 space-y-2.5">
                  {COHOST_SERVICES.map((s, i) => (
                    <li key={s} className="flex items-start gap-3 text-sm text-[#1C1917]" data-testid={`cohost-service-${i}`}>
                      <Check className="w-4 h-4 text-[#8A9A86] shrink-0 mt-0.5" strokeWidth={2} />
                      <span className="font-light">{s}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <button onClick={() => scrollToForm("Airbnb Co-Hosting")} data-testid="cohost-cta" className="inline-flex items-center gap-2 rounded-full bg-[#1C1917] text-white px-6 py-3 hover:bg-[#44403C] transition-colors duration-300 font-medium text-sm">
                    Explore Co-Hosting Partnership <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
                  </button>
                </div>
              </div>
            </Reveal>

            {/* Leasing */}
            <Reveal delay={0.08}>
              <div className="h-full rounded-2xl border border-[#E7E5E4] bg-[#FAFAF9] p-8 md:p-10 flex flex-col" data-testid="option-leasing">
                <Building2 className="w-6 h-6 text-[#8A9A86]" strokeWidth={1.5} />
                <p className="mt-4 text-xs tracking-[0.2em] uppercase font-medium text-[#8A9A86]">Option 2 · Furnished Rental &amp; Leasing Partnership</p>
                <h3 className="mt-3 text-2xl md:text-3xl tracking-tight font-light text-[#1C1917]">Reliable leasing. Professional property care.</h3>
                <p className="mt-4 text-[#44403C] font-light leading-relaxed">
                  Masaya Hospitality also partners with property owners through leasing arrangements
                  for professionally managed furnished accommodations serving business travelers,
                  relocating professionals, and extended-stay guests.
                </p>
                <ul className="mt-6 space-y-2.5">
                  {LEASING_SERVICES.map((s, i) => (
                    <li key={s} className="flex items-start gap-3 text-sm text-[#1C1917]" data-testid={`leasing-service-${i}`}>
                      <Check className="w-4 h-4 text-[#8A9A86] shrink-0 mt-0.5" strokeWidth={2} />
                      <span className="font-light">{s}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <button onClick={() => scrollToForm("Leasing Partnership")} data-testid="leasing-cta" className="inline-flex items-center gap-2 rounded-full bg-[#1C1917] text-white px-6 py-3 hover:bg-[#44403C] transition-colors duration-300 font-medium text-sm">
                    Discuss Leasing Opportunities <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
                  </button>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Why Partner */}
      <section className="px-6 md:px-12 pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto">
          <Reveal className="mb-14 max-w-3xl">
            <span className="text-xs sm:text-sm tracking-[0.2em] uppercase font-medium text-[#8A9A86]">Why partner with Masaya</span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-[#1C1917]">
              Experience you can trust
            </h2>
            <p className="mt-6 text-lg text-[#44403C] leading-relaxed font-light">
              We understand both sides of hospitality — protecting the homeowner's investment and
              creating a memorable guest experience. Our hands-on approach combines hosting
              experience, dedicated cleaning support, and personalized property management.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E7E5E4] border border-[#E7E5E4] rounded-2xl overflow-hidden">
            {TRUST_POINTS.map((p, i) => (
              <Reveal key={p.title} delay={(i % 3) * 0.06}>
                <div className="bg-[#FAFAF9] p-8 md:p-10 h-full" data-testid={`trust-${i}`}>
                  <p.icon className="w-6 h-6 text-[#8A9A86]" strokeWidth={1.5} />
                  <h3 className="mt-4 text-lg font-normal tracking-tight text-[#1C1917] leading-snug">{p.title}</h3>
                  {p.suffix && <p className="mt-1 text-xs text-[#8A9A86] font-light">{p.suffix}</p>}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Owner inquiry form */}
      <section ref={formRef} id="owner-form" className="px-6 md:px-12 pb-24 md:pb-32 scroll-mt-32">
        <div className="max-w-3xl mx-auto">
          <Reveal className="mb-8">
            <span className="text-xs sm:text-sm tracking-[0.2em] uppercase font-medium text-[#8A9A86]">Partnership consultation</span>
            <h2 className="mt-4 text-3xl sm:text-4xl tracking-tight font-light text-[#1C1917]">Let's talk about your property</h2>
            <p className="mt-4 text-[#44403C] font-light">
              Share a few details and our team will follow up within one business day. All inquiries
              are reviewed by Masaya Hospitality — no third parties, no obligation.
            </p>
          </Reveal>
          <OwnerForm />
        </div>
      </section>
    </div>
  );
}

// -------------------- Form --------------------
const inp =
  "w-full rounded-lg bg-white border border-[#E7E5E4] focus:outline-none focus:ring-1 focus:ring-[#8A9A86] focus:border-[#8A9A86] h-11 px-3 text-[#1C1917] text-sm";
const textarea =
  "w-full rounded-lg bg-white border border-[#E7E5E4] focus:outline-none focus:ring-1 focus:ring-[#8A9A86] focus:border-[#8A9A86] px-3 py-2 text-[#1C1917] text-sm";
const btn =
  "inline-flex items-center gap-2 rounded-full bg-[#1C1917] text-white px-7 py-3.5 hover:bg-[#44403C] transition-colors duration-300 font-medium text-sm disabled:opacity-60";

const PARTNERSHIP_OPTIONS = ["Airbnb Co-Hosting", "Leasing Partnership", "Not Sure Yet"];

function OwnerForm() {
  const empty = {
    name: "", email: "", phone: "",
    property_address: "", bedrooms: "",
    is_furnished: "Yes",
    partnership_interest: "Airbnb Co-Hosting",
    message: "",
  };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  // Listen for CTA preselection
  if (typeof window !== "undefined" && !window.__owner_preselect_wired) {
    window.__owner_preselect_wired = true;
    window.addEventListener("owners-preselect", (ev) => {
      const choice = ev.detail;
      if (PARTNERSHIP_OPTIONS.includes(choice)) {
        // Use a direct DOM query to update the select — simple & robust.
        const el = document.querySelector('[data-testid="owner-partnership"]');
        if (el) {
          el.value = choice;
          el.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }
    });
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, {
        name: f.name,
        email: f.email,
        phone: f.phone,
        inquiry_type: "owner",
        message: f.message || `Partnership inquiry: ${f.partnership_interest}`,
        property_address: f.property_address,
        bedrooms: f.bedrooms,
        is_furnished: f.is_furnished,
        partnership_interest: f.partnership_interest,
      });
      setDone(true);
      toast.success("Thanks — we'll be in touch soon.");
    } catch (err) {
      toast.error("Sorry — something went wrong. Please email hello@masayahospitality.com directly.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-[#E7E5E4] bg-white p-10 text-center" data-testid="owner-success">
        <CheckCircle2 className="w-10 h-10 mx-auto text-[#8A9A86]" strokeWidth={1.5} />
        <p className="mt-5 text-2xl tracking-tight font-normal text-[#1C1917]">Partnership request received</p>
        <p className="mt-3 text-[#44403C] font-light max-w-md mx-auto">
          Thanks{f.name ? `, ${f.name.split(" ")[0]}` : ""}. Our team will review your property details
          and follow up within one business day to schedule your consultation.
        </p>
        <button onClick={() => { setF(empty); setDone(false); }} className="mt-8 text-sm text-[#8A9A86] underline underline-offset-4">Submit another inquiry</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-[#E7E5E4] bg-white p-6 md:p-8 space-y-4" data-testid="owner-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Labeled label="Full name"><input required value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="owner-name" className={inp} /></Labeled>
        <Labeled label="Email address"><input required type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="owner-email" className={inp} /></Labeled>
        <Labeled label="Phone number"><input required value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="owner-phone" className={inp} placeholder="+1 512 555 0100" /></Labeled>
        <Labeled label="Property address"><input required value={f.property_address} onChange={(e) => set("property_address", e.target.value)} data-testid="owner-address" className={inp} placeholder="Street, city, ZIP" /></Labeled>
        <Labeled label="Number of bedrooms"><input required value={f.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} data-testid="owner-bedrooms" className={inp} placeholder="e.g. 3" /></Labeled>
        <Labeled label="Property currently furnished?">
          <select value={f.is_furnished} onChange={(e) => set("is_furnished", e.target.value)} data-testid="owner-furnished" className={inp}>
            <option>Yes</option><option>No</option>
          </select>
        </Labeled>
        <Labeled label="Partnership interest" className="md:col-span-2">
          <select value={f.partnership_interest} onChange={(e) => set("partnership_interest", e.target.value)} data-testid="owner-partnership" className={inp}>
            {PARTNERSHIP_OPTIONS.map((o) => <option key={o}>{o}</option>)}
          </select>
        </Labeled>
      </div>
      <Labeled label="Additional details">
        <textarea rows={4} value={f.message} onChange={(e) => set("message", e.target.value)} data-testid="owner-message" className={textarea} placeholder="Tell us about your property, goals, timeline, or any questions…" />
      </Labeled>
      <p className="text-xs text-[#78716C]">By submitting, you agree to be contacted about your partnership inquiry. Your information is never shared.</p>
      <button type="submit" disabled={busy} data-testid="owner-submit" className={btn}>{busy ? "Sending…" : "Request a Partnership Consultation"}</button>
    </form>
  );
}

function Labeled({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-xs text-[#78716C] block mb-1">{label}</label>
      {children}
    </div>
  );
}
